'use client';

import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import Image from '@/components/ui/CspImage';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Link } from '@/i18n/navigation';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import {
  ArrowLeft,
  Clock,
  Calendar,
  User,
  Twitter,
  Linkedin,
  Facebook,
  Link2,
  ChevronRight,
  Sparkles,
  Menu,
  X,
  List,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { rehypeStripUnsafe } from '@/lib/sanitize-hast';
import BlogCard from '@/components/landing/BlogCard';
import NewsletterSignup from '@/components/newsletter/NewsletterSignup';
import WaitlistModal from '@/components/WaitlistModal';

interface BlogPostClientProps {
  slug: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialPost?: Record<string, any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialRelatedPosts?: Record<string, any>[];
}

// Category color mapping - using landing page primary color
const categoryColors: Record<string, { bg: string; text: string }> = {
  'fashion-trends': { bg: 'bg-primary/10', text: 'text-primary' },
  'product-updates': { bg: 'bg-blue-500/10', text: 'text-blue-600' },
  'tutorials': { bg: 'bg-emerald-500/10', text: 'text-emerald-600' },
  'company-news': { bg: 'bg-violet-500/10', text: 'text-violet-600' },
  'ai-insights': { bg: 'bg-amber-500/10', text: 'text-amber-600' },
};

export default function BlogPostClient({ slug, initialPost, initialRelatedPosts }: BlogPostClientProps) {
  const t = useTranslations('blog');
  const tNav = useTranslations('nav');
  const tFooter = useTranslations('footer');
  const tCta = useTranslations('cta');
  const locale = useLocale();
  const isZh = locale === 'zh-TW';
  const postLive = useQuery(api.blogPosts.getPostBySlug, { slug });
  // Use live data when available, fall back to server-fetched initial data for SSR
  const post = postLive !== undefined ? postLive : initialPost;
  const relatedPostsLive = useQuery(
    api.blogPosts.getRelatedPosts,
    post ? { slug: post.slug, categorySlug: post.categorySlug, limit: 3 } : 'skip'
  );
  const relatedPosts = relatedPostsLive ?? initialRelatedPosts;
  const waitlistMode = useQuery(api.systemSettings.getWaitlistMode, {}) ?? false;
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeHeadingId, setActiveHeadingId] = useState<string>('');
  const [isTocOpen, setIsTocOpen] = useState(false);
  const articleRef = useRef<HTMLDivElement>(null);

  // Extract TOC headings from markdown content
  const tocHeadings = useMemo(() => {
    const content = isZh && post?.contentZh ? post.contentZh : post?.content;
    if (!content) return [];
    const headingRegex = /^(#{2,3})\s+(.+)$/gm;
    const headings: { level: number; text: string; id: string }[] = [];
    let match: RegExpExecArray | null;
    // biome-ignore lint/suspicious/noAssignInExpressions: idiomatic global-regex exec loop; splitting the assignment would duplicate the exec call
    while ((match = headingRegex.exec(content)) !== null) {
      const text = match[2].replace(/\*\*/g, '').replace(/\*/g, '').trim();
      const id = text
        .toLowerCase()
        .replace(/[^\w\s\u4e00-\u9fff\u3400-\u4dbf-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      headings.push({ level: match[1].length, text, id });
    }
    return headings;
  }, [post, isZh]);

  // Reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const article = articleRef.current;
      if (!article) return;
      const rect = article.getBoundingClientRect();
      const articleTop = rect.top + window.scrollY;
      const articleHeight = rect.height;
      const scrolled = window.scrollY - articleTop;
      const progress = Math.min(100, Math.max(0, (scrolled / (articleHeight - window.innerHeight)) * 100));
      setReadingProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Active heading tracking via IntersectionObserver
  useEffect(() => {
    if (tocHeadings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );
    const headingElements = document.querySelectorAll('.blog-prose h2[id], .blog-prose h3[id]');
    headingElements.forEach((el) => {
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, [tocHeadings]);

  const scrollToHeading = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
      setIsTocOpen(false);
    }
  }, []);

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = post?.title || 'Check out this article';

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
          '_blank'
        );
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
        break;
    }
  };

  // Only show loading if neither live nor initial data is available
  if (post === undefined) {
    return (
      <div className="landing-theme min-h-screen bg-background flex items-center justify-center font-body">
        <div className="animate-pulse text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (post === null) {
    return (
      <div className="landing-theme min-h-screen bg-background font-body">
        <nav aria-label="Main navigation" className="fixed top-0 w-full z-50 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link href="/" className="flex items-center">
              <Image
                src="/StyTrix_brand.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="block dark:hidden w-auto h-[2.25rem]"
                priority
              />
              <Image
                src="/StyTrix_brand_dark.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="hidden dark:block w-auto h-[2.25rem]"
                priority
              />
            </Link>
          </div>
        </nav>
        <main id="main-content" className="pt-32 pb-16 px-6 text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4 tracking-tight">{t('notFoundTitle')}</h1>
          <p className="text-muted-foreground mb-8">{t('notFoundDescription')}</p>
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 hover:shadow-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('backToBlog')}
          </Link>
        </main>
      </div>
    );
  }

  const colors = categoryColors[post.categorySlug] || categoryColors['fashion-trends'];
  const publishedDate = post.publishedAt ? new Date(post.publishedAt) : new Date(post.createdAt);

  return (
    <div className="landing-theme min-h-screen bg-background font-body">
      {/* Navigation - matching landing page */}
      <nav aria-label="Main navigation" className="fixed top-0 w-full z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link href="/" className="flex items-center">
              <Image
                src="/StyTrix_brand.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="block dark:hidden w-auto h-[2.25rem]"
                priority
              />
              <Image
                src="/StyTrix_brand_dark.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="hidden dark:block w-auto h-[2.25rem]"
                priority
              />
            </Link>

            {/* Navigation Links - matching landing page */}
            <div className="hidden md:flex items-center gap-8">
              <Link href="/documentation" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {tNav('documentation')}
              </Link>
              <Link href="/#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                {tNav('pricing')}
              </Link>
              <Link href="/blog" className="text-sm font-medium text-foreground transition-colors">
                {tNav('blog')}
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <LanguageSwitcher className="hidden md:flex" />
              {waitlistMode ? (
                <button
                  onClick={() => setIsWaitlistOpen(true)}
                  className="hidden sm:block px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 hover:shadow-lg whitespace-nowrap"
                >
                  {tNav('joinWaitlist')}
                </button>
              ) : (
                <a
                  href="/sign-in"
                  className="hidden sm:block px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 hover:shadow-lg"
                >
                  {tNav('signIn')}
                </a>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2.5 rounded-lg hover:bg-secondary transition-colors"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-lg border-b border-border shadow-elevated-lg transition-all duration-300 ${
            isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
        >
          <div className="px-6 py-6 space-y-4">
            <Link
              href="/documentation"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border"
            >
              {tNav('documentation')}
            </Link>
            <Link
              href="/#pricing"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border"
            >
              {tNav('pricing')}
            </Link>
            <Link
              href="/blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border"
            >
              {tNav('blog')}
            </Link>
            {waitlistMode ? (
              <button
                onClick={() => { setIsWaitlistOpen(true); setIsMobileMenuOpen(false); }}
                className="w-full mt-4 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all"
              >
                {tNav('joinWaitlist')}
              </button>
            ) : (
              <a
                href="/sign-in"
                className="block w-full mt-4 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all text-center"
              >
                {tNav('signIn')}
              </a>
            )}
            <div className="flex items-center justify-center pt-4 border-t border-border">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </nav>
      <main id="main-content">

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-[3px]">
        <div
          className="h-full bg-gradient-to-r from-primary to-primary/70 transition-[width] duration-150 ease-out"
          ref={element => { if (element) element.style.width = `${readingProgress}%` }}
        />
      </div>

      {/* Article Header */}
      <header className="pt-32 pb-8 px-6 bg-gradient-to-b from-secondary to-background">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-muted-foreground mb-8 overflow-hidden">
            <Link href="/" className="hover:text-primary transition-colors whitespace-nowrap shrink-0">
              {t('home')}
            </Link>
            <ChevronRight className="w-4 h-4 shrink-0" />
            <Link href="/blog" className="hover:text-primary transition-colors whitespace-nowrap shrink-0">
              {tNav('blog')}
            </Link>
            <ChevronRight className="w-4 h-4 shrink-0" />
            <span className="text-foreground font-medium truncate">{isZh && post.titleZh ? post.titleZh : post.title}</span>
          </nav>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${colors.bg} ${colors.text}`}>
              {post.category?.name || post.categorySlug}
            </span>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <time dateTime={publishedDate.toISOString()}>
                {publishedDate.toLocaleDateString(isZh ? 'zh-TW' : 'en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </time>
            </div>
            {post.readingTimeMinutes && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                {t('minRead', { minutes: post.readingTimeMinutes })}
              </div>
            )}
            {post.aiGenerated && (
              <div className="flex items-center gap-1 text-sm text-primary">
                <Sparkles className="w-4 h-4" />
                {t('aiGenerated')}
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight tracking-tight">
            {isZh && post.titleZh ? post.titleZh : post.title}
          </h1>

          {/* Excerpt */}
          {(isZh ? (post.excerptZh || post.excerpt) : post.excerpt) && (
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">{isZh && post.excerptZh ? post.excerptZh : post.excerpt}</p>
          )}

          {/* Author & Share */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-8 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-transparent hover:ring-primary/20 transition-all duration-300">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground">{post.authorName || 'StyTrix Team'}</div>
                <div className="text-sm text-muted-foreground">{t('author')}</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">{t('shareArticle')}:</span>
              <button
                onClick={() => handleShare('twitter')}
                className="p-2.5 rounded-lg hover:bg-card hover:scale-110 transition-all duration-200"
                aria-label="Share on Twitter"
              >
                <Twitter className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="p-2.5 rounded-lg hover:bg-card hover:scale-110 transition-all duration-200"
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors" />
              </button>
              <button
                onClick={() => handleShare('facebook')}
                className="p-2.5 rounded-lg hover:bg-card hover:scale-110 transition-all duration-200"
                aria-label="Share on Facebook"
              >
                <Facebook className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors" />
              </button>
              <button
                onClick={() => handleShare('copy')}
                className="p-2.5 rounded-lg hover:bg-card hover:scale-110 transition-all duration-200"
                aria-label="Copy link"
              >
                <Link2 className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      {(post.r2FeaturedImageUrl || post.featuredImageUrl) && (
        <div className="px-6 mb-12">
          <div className="max-w-5xl mx-auto">
            <div className="relative aspect-[21/9] rounded-2xl overflow-hidden shadow-elevated-lg">
              <Image
                src={post.r2FeaturedImageUrl || post.featuredImageUrl || ''}
                alt={post.featuredImageAlt || post.title}
                fill
                className="object-cover"
                sizes="(max-width: 1200px) 100vw, 1200px"
                priority
              />
            </div>
          </div>
        </div>
      )}

      {/* Article Content with Sticky Sidebar CTA */}
      <article className="px-6 pb-16" ref={articleRef}>
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1fr,280px] gap-12">
            {/* Main Content */}
            <div className="max-w-3xl">
              {/* Table of Contents */}
              {tocHeadings.length > 2 && (
                <div className="mb-10 rounded-xl border border-border bg-card/50 overflow-hidden">
                  <button
                    onClick={() => setIsTocOpen(!isTocOpen)}
                    className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <List className="w-4 h-4 text-primary" />
                      <span className="font-semibold text-foreground text-sm">
                        {isZh ? '目錄' : 'Table of Contents'}
                      </span>
                      <span className="text-xs text-muted-foreground">({tocHeadings.filter(h => h.level === 2).length} {isZh ? '章節' : 'sections'})</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isTocOpen ? 'rotate-90' : ''}`} />
                  </button>
                  <div className={`transition-all duration-300 ease-in-out ${isTocOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'} overflow-y-auto`}>
                    <nav aria-label="On this page" className="px-6 pb-4 space-y-1 border-l-[3px] border-primary/20 ml-6">
                      {tocHeadings.map((heading) => (
                        <button
                          key={heading.id}
                          onClick={() => scrollToHeading(heading.id)}
                          className={`block w-full text-left text-sm py-1.5 transition-colors duration-200 ${
                            heading.level === 3 ? 'pl-4' : 'pl-0 font-medium'
                          } ${
                            activeHeadingId === heading.id
                              ? 'text-primary'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {heading.text}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              )}

              <div className="blog-prose prose prose-lg dark:prose-invert max-w-none
                prose-headings:font-display prose-headings:text-foreground prose-headings:scroll-mt-24
                prose-h2:text-2xl prose-h2:md:text-3xl prose-h2:mt-14 prose-h2:mb-5 prose-h2:pb-3 prose-h2:border-b prose-h2:border-border/50
                prose-h3:text-xl prose-h3:md:text-2xl prose-h3:mt-10 prose-h3:mb-4
                prose-p:text-[17px] prose-p:leading-[1.85] prose-p:text-muted-foreground prose-p:mb-6
                prose-a:text-primary prose-a:no-underline prose-a:border-b prose-a:border-primary/30 hover:prose-a:border-primary prose-a:transition-colors
                prose-strong:text-foreground prose-strong:font-semibold
                prose-code:text-primary prose-code:bg-primary/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                prose-pre:bg-card prose-pre:border prose-pre:border-border prose-pre:rounded-xl
                prose-li:text-[17px] prose-li:leading-[1.85] prose-li:text-muted-foreground prose-li:marker:text-primary/60
                prose-ol:marker:text-primary/60 prose-ol:marker:font-semibold
                prose-blockquote:border-l-primary/40 prose-blockquote:bg-primary/5 prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:px-1
                prose-img:rounded-xl prose-img:shadow-lg
                prose-hr:border-border/50">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeStripUnsafe]}
                  components={{
                    h2: ({ children, ...props }) => {
                      const text = String(children).replace(/\*\*/g, '').replace(/\*/g, '');
                      const id = text.toLowerCase().replace(/[^\w\s\u4e00-\u9fff\u3400-\u4dbf-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
                      return <h2 id={id} {...props}>{children}</h2>;
                    },
                    h3: ({ children, ...props }) => {
                      const text = String(children).replace(/\*\*/g, '').replace(/\*/g, '');
                      const id = text.toLowerCase().replace(/[^\w\s\u4e00-\u9fff\u3400-\u4dbf-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
                      return <h3 id={id} {...props}>{children}</h3>;
                    },
                  }}
                >{isZh && post.contentZh ? post.contentZh : post.content}</ReactMarkdown>
              </div>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-border">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag: string, index: number) => (
                      <span
                        // biome-ignore lint/suspicious/noArrayIndexKey: tag list comes straight from the post record and is never reordered; tags are not guaranteed unique
                        key={index}
                        className="px-3 py-1 text-sm bg-card border border-border rounded-full text-muted-foreground hover:border-primary/30 hover:text-foreground transition-colors duration-200"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sticky Sidebar - Desktop Only */}
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-6">
                {/* Sidebar TOC */}
                {tocHeadings.length > 2 && (
                  <nav aria-label="On this page" className="p-4 bg-card/50 rounded-xl border border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{isZh ? '本文目錄' : 'On this page'}</p>
                    <div className="space-y-0.5 max-h-[300px] overflow-y-auto">
                      {tocHeadings.filter(h => h.level === 2).map((heading) => (
                        <button
                          key={heading.id}
                          onClick={() => scrollToHeading(heading.id)}
                          className={`block w-full text-left text-xs py-1.5 pr-2 transition-colors duration-200 truncate ${
                            activeHeadingId === heading.id
                              ? 'text-primary font-medium'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {heading.text}
                        </button>
                      ))}
                    </div>
                  </nav>
                )}
                <div className="p-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl border border-primary/20 shadow-elevated">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium text-primary">{t('sidebarCtaBadge')}</span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2 tracking-tight">
                    {t('sidebarCtaTitle')}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('sidebarCtaDescription')}
                  </p>
                  {waitlistMode ? (
                    <button
                      onClick={() => setIsWaitlistOpen(true)}
                      className="block w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 hover:shadow-lg text-center"
                    >
                      {t('sidebarCtaButtonWaitlist')}
                    </button>
                  ) : (
                    <a
                      href="/sign-in"
                      className="block w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 hover:shadow-lg text-center"
                    >
                      {t('sidebarCtaButton')}
                    </a>
                  )}
                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    {waitlistMode ? t('sidebarCtaNoteWaitlist') : t('sidebarCtaNote')}
                  </p>
                </div>

                {/* Social Share - Sidebar */}
                <div className="p-4 bg-card rounded-xl border border-border">
                  <p className="text-sm font-medium text-foreground mb-3">{t('shareArticle')}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleShare('twitter')}
                      className="flex-1 p-2 rounded-lg hover:bg-secondary hover:scale-110 transition-all duration-200 flex items-center justify-center"
                      aria-label="Share on Twitter"
                    >
                      <Twitter className="w-4 h-4 text-muted-foreground hover:text-primary" />
                    </button>
                    <button
                      onClick={() => handleShare('linkedin')}
                      className="flex-1 p-2 rounded-lg hover:bg-secondary hover:scale-110 transition-all duration-200 flex items-center justify-center"
                      aria-label="Share on LinkedIn"
                    >
                      <Linkedin className="w-4 h-4 text-muted-foreground hover:text-primary" />
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="flex-1 p-2 rounded-lg hover:bg-secondary hover:scale-110 transition-all duration-200 flex items-center justify-center"
                      aria-label="Share on Facebook"
                    >
                      <Facebook className="w-4 h-4 text-muted-foreground hover:text-primary" />
                    </button>
                    <button
                      onClick={() => handleShare('copy')}
                      className="flex-1 p-2 rounded-lg hover:bg-secondary hover:scale-110 transition-all duration-200 flex items-center justify-center"
                      aria-label="Copy link"
                    >
                      <Link2 className="w-4 h-4 text-muted-foreground hover:text-primary" />
                    </button>
                  </div>
                </div>

                {/* Newsletter Signup */}
                <NewsletterSignup variant="sidebar" source="blog-post" />
              </div>
            </aside>
          </div>

          {/* Mobile CTA - Shows only on mobile */}
          <div className="lg:hidden mt-12 p-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-2xl border border-primary/20">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-primary">{t('sidebarCtaBadge')}</span>
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2 tracking-tight">
              {t('sidebarCtaTitleMobile')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('sidebarCtaDescription')}
            </p>
            {waitlistMode ? (
              <button
                onClick={() => setIsWaitlistOpen(true)}
                className="block w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 hover:shadow-lg text-center"
              >
                {t('sidebarCtaButtonWaitlist')}
              </button>
            ) : (
              <a
                href="/sign-in"
                className="block w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 hover:shadow-lg text-center"
              >
                {t('sidebarCtaButton')}
              </a>
            )}
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts && relatedPosts.length > 0 && (
        <section className="py-20 px-6 bg-secondary border-t border-border">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center tracking-tight">
              {t('relatedArticles')}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <BlogCard
                  key={relatedPost._id}
                  title={relatedPost.title}
                  titleZh={relatedPost.titleZh}
                  slug={relatedPost.slug}
                  excerpt={relatedPost.excerpt}
                  excerptZh={relatedPost.excerptZh}
                  featuredImageUrl={relatedPost.r2FeaturedImageUrl || relatedPost.featuredImageUrl}
                  featuredImageAlt={relatedPost.featuredImageAlt}
                  categoryName={relatedPost.category?.name || relatedPost.categorySlug}
                  categorySlug={relatedPost.categorySlug}
                  categoryColor={relatedPost.category?.color}
                  readingTimeMinutes={relatedPost.readingTimeMinutes}
                  publishedAt={relatedPost.publishedAt}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Explore StyTrix — SEO internal linking to tools, glossary, compare, use-cases */}
      <section className="py-12 px-6 bg-background border-t border-border">
        <div className="max-w-7xl mx-auto space-y-10">
          {/* Tools */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6 text-center tracking-tight">
              {isZh ? '探索 StyTrix 工具' : 'Explore StyTrix Tools'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link href="/tools/ai-fashion-generator" className="p-4 bg-card rounded-xl border border-border hover:border-primary/30 hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300 group">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm">{isZh ? 'AI 時尚生成器' : 'AI Fashion Generator'}</h3>
                <p className="text-xs text-muted-foreground mt-1">{isZh ? '文字轉時尚設計' : 'Text to fashion design'}</p>
              </Link>
              <Link href="/tools/lora-training" className="p-4 bg-card rounded-xl border border-border hover:border-primary/30 hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300 group">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm">{isZh ? 'LoRA 訓練' : 'LoRA Training'}</h3>
                <p className="text-xs text-muted-foreground mt-1">{isZh ? '自訂品牌 AI 模型' : 'Custom brand AI models'}</p>
              </Link>
              <Link href="/tools/virtual-fashion-sampling" className="p-4 bg-card rounded-xl border border-border hover:border-primary/30 hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300 group">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm">{isZh ? '虛擬打樣' : 'Virtual Sampling'}</h3>
                <p className="text-xs text-muted-foreground mt-1">{isZh ? '取代實體樣品' : 'Replace physical samples'}</p>
              </Link>
              <Link href="/tools/real-time-collaboration" className="p-4 bg-card rounded-xl border border-border hover:border-primary/30 hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300 group">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm">{isZh ? '即時協作' : 'Real-Time Collaboration'}</h3>
                <p className="text-xs text-muted-foreground mt-1">{isZh ? '即時共同設計' : 'Design together live'}</p>
              </Link>
            </div>
            <div className="text-center mt-4">
              <Link href="/tools" className="text-sm text-primary hover:text-primary/80 transition-colors link-underline">{isZh ? '查看所有工具 →' : 'View all tools →'}</Link>
            </div>
          </div>

          {/* Use Cases */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 text-center tracking-tight">
              {isZh ? 'AI 時尚應用場景' : 'AI Fashion Use Cases'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Link href="/use-cases/concept-creation" className="p-3 bg-card rounded-lg border border-border hover:border-primary/30 hover:shadow-sm transition-all duration-300 group">
                <span className="font-medium text-foreground group-hover:text-primary transition-colors text-sm">{isZh ? '概念創作' : 'Concept Creation'}</span>
              </Link>
              <Link href="/use-cases/virtual-sampling" className="p-3 bg-card rounded-lg border border-border hover:border-primary/30 hover:shadow-sm transition-all duration-300 group">
                <span className="font-medium text-foreground group-hover:text-primary transition-colors text-sm">{isZh ? '虛擬打樣' : 'Virtual Sampling'}</span>
              </Link>
              <Link href="/use-cases/collection-planning" className="p-3 bg-card rounded-lg border border-border hover:border-primary/30 hover:shadow-sm transition-all duration-300 group">
                <span className="font-medium text-foreground group-hover:text-primary transition-colors text-sm">{isZh ? '系列規劃' : 'Collection Planning'}</span>
              </Link>
              <Link href="/use-cases/lookbook-generation" className="p-3 bg-card rounded-lg border border-border hover:border-primary/30 hover:shadow-sm transition-all duration-300 group">
                <span className="font-medium text-foreground group-hover:text-primary transition-colors text-sm">{isZh ? 'Lookbook 生成' : 'Lookbook Generation'}</span>
              </Link>
              <Link href="/use-cases/brand-consistency" className="p-3 bg-card rounded-lg border border-border hover:border-primary/30 hover:shadow-sm transition-all duration-300 group">
                <span className="font-medium text-foreground group-hover:text-primary transition-colors text-sm">{isZh ? '品牌一致性' : 'Brand Consistency'}</span>
              </Link>
            </div>
            <div className="text-center mt-3">
              <Link href="/use-cases" className="text-sm text-primary hover:text-primary/80 transition-colors link-underline">{isZh ? '查看所有應用場景 →' : 'View all use cases →'}</Link>
            </div>
          </div>

          {/* Glossary + Compare */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3 tracking-tight">
                {isZh ? 'AI 時尚詞彙表' : 'AI Fashion Glossary'}
              </h3>
              <div className="space-y-2">
                <Link href="/glossary/ai-fashion-design" className="block text-sm text-muted-foreground hover:text-primary transition-colors">{isZh ? '什麼是 AI 時尚設計？' : 'What is AI Fashion Design?'}</Link>
                <Link href="/glossary/lora-training" className="block text-sm text-muted-foreground hover:text-primary transition-colors">{isZh ? '什麼是 LoRA 訓練？' : 'What is LoRA Training?'}</Link>
                <Link href="/glossary/virtual-fashion-sampling" className="block text-sm text-muted-foreground hover:text-primary transition-colors">{isZh ? '什麼是虛擬時尚打樣？' : 'What is Virtual Fashion Sampling?'}</Link>
                <Link href="/glossary/generative-ai" className="block text-sm text-muted-foreground hover:text-primary transition-colors">{isZh ? '什麼是生成式 AI？' : 'What is Generative AI?'}</Link>
                <Link href="/glossary" className="block text-sm text-primary hover:text-primary/80 transition-colors link-underline mt-2">{isZh ? '查看完整詞彙表 →' : 'View full glossary →'}</Link>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3 tracking-tight">
                {isZh ? '比較 StyTrix' : 'Compare StyTrix'}
              </h3>
              <div className="space-y-2">
                <Link href="/compare/stytrix-vs-midjourney" className="block text-sm text-muted-foreground hover:text-primary transition-colors">StyTrix vs Midjourney</Link>
                <Link href="/compare/stytrix-vs-dalle" className="block text-sm text-muted-foreground hover:text-primary transition-colors">StyTrix vs DALL-E</Link>
                <Link href="/compare/stytrix-vs-clo3d" className="block text-sm text-muted-foreground hover:text-primary transition-colors">StyTrix vs CLO 3D</Link>
                <Link href="/compare/stytrix-vs-fashionai" className="block text-sm text-muted-foreground hover:text-primary transition-colors">StyTrix vs FashionAI</Link>
                <Link href="/compare" className="block text-sm text-primary hover:text-primary/80 transition-colors link-underline mt-2">{isZh ? '查看所有比較 →' : 'View all comparisons →'}</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight">
            {tCta('sectionTitle')}
          </h2>
          <p className="text-white/90 text-lg mb-8">
            {tCta('sectionSubtitle')}
          </p>
          {waitlistMode ? (
            <button
              onClick={() => setIsWaitlistOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary rounded-lg font-semibold text-lg hover:bg-white/95 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
            >
              {tCta('floatingButtonWaitlist')}
            </button>
          ) : (
            <a
              href="/sign-in"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary rounded-lg font-semibold text-lg hover:bg-white/95 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
            >
              {tCta('floatingButton')}
            </a>
          )}
        </div>
      </section>

      </main>
      {/* Footer - matching landing page */}
      <footer className="py-16 px-6 bg-secondary border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link href="/" className="flex items-center">
              <Image
                src="/StyTrix_brand.png"
                alt="StyTrix"
                width={120}
                height={32}
                className="block dark:hidden w-auto h-[2rem]"
              />
              <Image
                src="/StyTrix_brand_dark.png"
                alt="StyTrix"
                width={120}
                height={32}
                className="hidden dark:block w-auto h-[2rem]"
              />
            </Link>
            <div className="flex items-center gap-4 md:gap-8">
              <Link href="/#tools" className="text-sm text-muted-foreground hover:text-foreground transition-colors link-underline">
                {tNav('features')}
              </Link>
              <Link href="/#use-cases" className="text-sm text-muted-foreground hover:text-foreground transition-colors link-underline">
                {tNav('useCases')}
              </Link>
              <Link href="/#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors link-underline">
                {tNav('pricing')}
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              {tFooter('copyright', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </footer>

      {/* Waitlist Modal */}
      <WaitlistModal isOpen={isWaitlistOpen} onClose={() => setIsWaitlistOpen(false)} />
    </div>
  );
}
